import { useCallback, useEffect, useRef } from "react";

import { trackRumNativeBridgeRetriesExhausted } from "@app/rumMonitoring";

const RETRY_BASE_DELAY_MS = 1000;
const RETRY_BACKOFF_FACTOR = 2;
const MAX_RETRIES = 5;

export type UseTrackOnceWithRetryParams = {
  /**
   * Attempt to dispatch only when this is `true`. Lets the wrapper hook
   * gate on its own readiness signals (e.g. `accessCode` present,
   * `loading` false). The shared hook silently skips while `enabled`
   * is `false`; the wrapper owns any user-facing "waiting" logs.
   */
  enabled: boolean;
  /**
   * Bail out completely (no dispatch, no retry) when this is truthy.
   * The wrapper owns the error log; this hook just respects the flag.
   */
  error?: Error;
  /**
   * The actual analytics dispatch. Should resolve to `true` on
   * successful native-bridge delivery, `false` when the bridge is
   * unavailable (triggers retry). Never rejects.
   */
  dispatch: () => Promise<boolean>;
  /** Prefix for retry / result log lines (e.g. `[CareTabAnalytics]`). */
  logPrefix: string;
  /**
   * Optional metadata merged into the retry / result log payloads
   * (e.g. `{ screenName: "care_tab" }`). Useful for grep-ability in
   * the WebView devtools output.
   */
  logContext?: Record<string, unknown>;
};

/**
 * Shared "fire exactly once, retry on bridge unavailability" hook for
 * native-analytics page-view style events. Owns the once-only +
 * in-flight + unmount + exponential-backoff state machine; the wrapper
 * hook owns the readiness conditions and any caller-specific logging.
 *
 * Behavior:
 *   - Skips silently while `enabled` is `false` or `error` is truthy.
 *   - When ready and not yet sent, calls `dispatch()`.
 *   - On `false` return value, schedules an exponential-backoff retry
 *     (1s, 2s, 4s, 8s, 16s — up to 5 attempts).
 *   - On `true` return value, marks the event as sent and never fires
 *     again for the lifetime of the component.
 *   - On unmount, clears any pending retry and stops further attempts.
 */
export function useTrackOnceWithRetry({
  enabled,
  error,
  dispatch,
  logPrefix,
  logContext,
}: UseTrackOnceWithRetryParams): void {
  const hasSentRef = useRef(false);
  const isSendingRef = useRef(false);
  const hasUnmountedRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Keep latest dispatch / logContext available to the recursive retry
  // closure without re-creating attemptTracking on every render.
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const logContextRef = useRef(logContext);
  logContextRef.current = logContext;

  const clearPendingRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }
  }, []);

  const attemptTracking = useCallback(async () => {
    if (hasSentRef.current || isSendingRef.current) {
      return;
    }

    isSendingRef.current = true;

    try {
      const dispatched = await dispatchRef.current();

      if (hasUnmountedRef.current) {
        return;
      }

      if (dispatched) {
        hasSentRef.current = true;
        retryCountRef.current = 0;
      } else if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const retryDelayMs =
          RETRY_BASE_DELAY_MS *
          RETRY_BACKOFF_FACTOR ** (retryCountRef.current - 1);
        console.info(`${logPrefix} retrying`, {
          attempt: retryCountRef.current,
          maxRetries: MAX_RETRIES,
          retryDelayMs,
          ...logContextRef.current,
        });

        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = undefined;
          if (!hasUnmountedRef.current) {
            void attemptTracking();
          }
        }, retryDelayMs);
      } else {
        // Out of retries. Nothing is user-visible here — the page works fine,
        // the native app just never hears about it — so RUM is the only place
        // a persistently broken bridge can show up.
        trackRumNativeBridgeRetriesExhausted({
          logPrefix,
          attempts: retryCountRef.current,
        });
      }

      console.info(`${logPrefix} tracking result`, {
        dispatched,
        ...logContextRef.current,
      });
    } finally {
      isSendingRef.current = false;
    }
  }, [logPrefix]);

  useEffect(
    () => () => {
      hasUnmountedRef.current = true;
      clearPendingRetry();
    },
    [clearPendingRetry]
  );

  useEffect(() => {
    if (!hasSentRef.current && enabled && !error) {
      void attemptTracking();
    }

    return clearPendingRetry;
  }, [attemptTracking, clearPendingRetry, enabled, error]);
}
